import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import SEOHead from './SEOHead';
import { createOrGetProductChat } from '../utils/chat';
import useAuthStore from '../store/authStore';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import ProductViewSkeleton from './ProductViewSkeleton';
import SellerDetailsSkeleton from './SellerDetailsSkeleton';
import ProductCardSkeleton from './ProductCardSkeleton';
import { apiGet, getImageUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

// Add the paymentIcons array from Footer for reuse
const paymentIconsFooter = [
  { label: 'Visa', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1a237e" fontWeight="bold">VISA</text></svg>) },
  { label: 'MasterCard', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><circle cx="18" cy="16" r="8" fill="#ff9800"/><circle cx="30" cy="16" r="8" fill="#e53935" fillOpacity=".8"/></svg>) },
  { label: 'PayPal', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1565c0" fontWeight="bold">PayPal</text></svg>) },
  { label: 'Wise', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#00bfae" fontWeight="bold">WISE</text></svg>) },
  { label: 'Bitcoin', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><circle cx="24" cy="16" r="8" fill="#ffb300"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#fff" fontWeight="bold">₿</text></svg>) },
];

// Mock related products (same category, 24 items)
const relatedProducts = Array.from({ length: 24 }).map((_, i) => ({
  image: `https://randomuser.me/api/portraits/women/${32 + i}.jpg`,
  platform: 'instagram',
  title: `${10 + i}K Followers Meow | Cats | Pets Insta Account ${i + 1}`,
  category: 'Pets & Animals',
  followers: 10000 + i * 1000,
  monetized: i % 2 === 0,
  oldPrice: 200 + i * 10,
  price: 150 + i * 8,
  onSale: i % 2 === 1,
}));

function ProductView() {
  const { slug } = useParams();
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedProductsLoading, setRelatedProductsLoading] = useState(false);
  const [relatedPage, setRelatedPage] = useState(0);
  const PRODUCTS_PER_PAGE = 8; // 2 rows of 4 products on desktop, 4 rows of 2 products on mobile
  const totalPages = Math.ceil(relatedProducts.length / PRODUCTS_PER_PAGE);
  const { user } = useAuthStore();
  const [contactLoading, setContactLoading] = useState(false);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [sellerUser, setSellerUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [sliderIndex, setSliderIndex] = useState(0);
  const isMobileCategory = (product?.accountType === 'tiktok' || product?.accountType === 'instagram');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isInCart, setIsInCart] = useState(false);

  // Logic for description truncation
  const description = product?.description || '';
  const isLongDescription = description.length > 250;
  const truncatedDescription = isLongDescription ? `${description.substring(0, 250)}...` : description;

  // Map backend fields to UI fields (move this up so useMemo can use them)
  const mainImage = product?.mainImage ? getImageUrl(product.mainImage) : '';
  const gallery = Array.isArray(product?.galleryImages)
    ? product.galleryImages.map(img => getImageUrl(img))
    : [];

  // useMemo for allImages at the top level
  const allImages = useMemo(() => {
    const imgs = [];
    if (mainImage) imgs.push(mainImage);
    if (Array.isArray(gallery)) imgs.push(...gallery);
    return imgs;
  }, [mainImage, gallery]);

  useEffect(() => {
    setLoading(true);
    
    // Intelligently detect if slug is an ObjectId or a slug
    const isObjectId = /^[a-f\d]{24}$/i.test(slug);
    
    // Choose the appropriate API endpoint
    const apiUrl = isObjectId 
      ? `/api/products/${slug}?t=${Date.now()}`
      : `/api/products/slug/${slug}?t=${Date.now()}`;
    
    apiGet(apiUrl)
      .then(res => {
        if (!res.ok) {
          throw new Error('Product not found');
        }
        return res.json();
      })
      .then(data => {
        console.log('API Response:', data);
        console.log('product.userId from API:', data.userId);
        setProduct(data);
        setLoading(false);
        // Fetch related products by category, excluding current product
        if (data.accountType) {
          setRelatedProductsLoading(true);
          apiGet(`/api/products?accountType=${encodeURIComponent(data.accountType)}&status=published`)
            .then(res => res.json())
            .then(list => {
              // Exclude the current product and only show published
              setRelatedProducts(
                Array.isArray(list)
                  ? list.filter(p => p._id !== data._id && p.status === 'published')
                  : []
              );
              setRelatedProductsLoading(false);
            })
            .catch(err => {
              console.error('Error fetching related products:', err);
              setRelatedProductsLoading(false);
            });
        }
      })
      .catch(err => {
        setError('Failed to load product.');
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    async function fetchSellerDetails() {
      if (product && product.userId) {
        console.log('product.userId:', product.userId);
        
        // If userId is already populated (object), use it directly
        if (typeof product.userId === 'object' && product.userId.username) {
          console.log('Using populated userId data directly');
          setSellerDetails({
            username: product.userId.username,
            avatar: product.userId.avatar,
            verified: product.userId.verified,
            rating: product.userId.rating,
            reviews: product.userId.reviews,
            joined: product.userId.joined,
            deals: product.userId.deals,
            total: product.userId.total,
            email: product.userId.email,
            uid: product.userId.uid
          });
          setSellerUser(product.userId);
          return;
        }
        
        // Fallback: If userId is a string (MongoDB ID), fetch user data
        if (typeof product.userId === 'string') {
          try {
            const res = await apiGet(`/api/users/${product.userId}`);
            if (!res.ok) throw new Error('Failed to fetch seller user from backend');
            const sellerUser = await res.json();
            console.log('Seller user from backend:', sellerUser);
            const firebaseUid = sellerUser.firebaseUid || sellerUser.uid;
            if (!firebaseUid) {
              setSellerDetails(null);
              console.warn('No firebaseUid found in seller user document');
              return;
            }
            // Now fetch Firestore user by firebaseUid
            getDoc(doc(db, 'users', firebaseUid)).then(sellerDoc => {
              if (sellerDoc.exists()) {
                setSellerDetails(sellerDoc.data());
                setSellerUser(sellerUser);
                console.log('Seller details loaded from Firestore:', sellerDoc.data());
              } else {
                setSellerDetails(null);
                console.warn('Seller details not found in Firestore for firebaseUid:', firebaseUid);
              }
            }).catch((err) => {
              setSellerDetails(null);
              console.error('Error loading seller details from Firestore:', err);
            });
          } catch (err) {
            setSellerDetails(null);
            console.error('Error fetching seller user from backend:', err);
          }
        }
      }
    }
    fetchSellerDetails();
  }, [product]);

  useEffect(() => {
    setSliderIndex(0);
  }, [product?._id]);

  useEffect(() => {
    // Check if this product is already in wishlist
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    setIsWishlisted(wishlist.some(item => item._id === product?._id));
    // Check if this product is already in cart
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setIsInCart(cart.some(item => item._id === product?._id));
  }, [product?._id]);

  if (loading) {
    return (
      <div className={`min-h-screen flex flex-col items-center py-4 sm:py-6 md:py-8 px-4 sm:px-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
      }`}>
        <div className="w-full max-w-[1495px] mx-auto flex flex-col xl:flex-row gap-8">
          <ProductViewSkeleton />
          <SellerDetailsSkeleton />
        </div>
      </div>
    );
  }
  if (error || !product) {
    return <div className={`min-h-screen flex items-center justify-center text-xl transition-colors duration-300 ${
      isDarkMode ? 'text-red-400' : 'text-red-600'
    }`}>{error || 'Product not found.'}</div>;
  }

  // Map backend fields to UI fields
  const stats = product.stats || {};

  // Use populated userId for seller details
  const seller = product.userId ? {
    uid: product.userId.uid,
    email: product.userId.email,
    name: product.userId.username || product.userId.name || 'Unknown Seller',
    avatar: product.userId.avatar || '/images/default-avatar.png',
    username: product.userId.username || 'Unknown Seller',
    verified: product.userId.verified || false,
    rating: product.userId.rating || 5,
    reviews: product.userId.reviews || 0,
    joined: product.userId.joined || 'N/A',
    deals: product.userId.deals || 0,
    total: product.userId.total || 0,
  } : {
    uid: '',
    email: '',
    name: 'Unknown Seller',
    avatar: '/images/default-avatar.png',
    username: 'Unknown Seller',
    verified: false,
    rating: 5,
    reviews: 0,
    joined: 'N/A',
    deals: 0,
    total: 0,
  };

  const handleContactSeller = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!product.userId) {
      alert('Seller details not found. Please try again later.');
      return;
    }
    setContactLoading(true);
    try {
      const productData = {
        id: product._id || product.id || 'unknown',
        name: product.title || 'Unknown Product',
        image: product.mainImage?.startsWith('http')
          ? product.mainImage
          : product.mainImage
            ? `/uploads/${product.mainImage}`
            : '',
        price: product.salePrice || product.price || '',
      };
      const buyerData = {
        uid: user.uid || 'unknown',
        email: user.email || 'unknown',
        name: user.username || user.displayName || user.name || user.email || 'Unknown Buyer',
      };
      // Use seller data directly from product.userId
      const sellerData = {
        uid: product.userId.uid || 'unknown',
        email: product.userId.email || 'unknown',
        name: product.userId.username || 'Unknown Seller',
      };
      
      console.log('[ProductView] Contact seller clicked with:', { productData, buyerData, sellerData });
      const conversationId = await createOrGetProductChat(productData, buyerData, sellerData);
      console.log('[ProductView] Got conversation ID:', conversationId);
      setContactLoading(false);
      navigate(`/messages?room=${conversationId}`);
    } catch (err) {
      console.error('[ProductView] Error creating/getting chat:', err);
      setContactLoading(false);
      alert('Failed to start chat. Please try again.');
    }
  };

  const openModal = (idx) => {
    setModalIndex(idx);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);
  const prevImage = () => setModalIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  const nextImage = () => setModalIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const handleSliderPrev = () => setSliderIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  const handleSliderNext = () => setSliderIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const handleToggleWishlist = () => {
    if (!product) return;
    let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const index = wishlist.findIndex(item => item._id === product._id);
    if (index !== -1) {
      wishlist.splice(index, 1);
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      setIsWishlisted(false);
      toast.info('Removed from wishlist!');
    } else {
      wishlist.push(product);
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      setIsWishlisted(true);
      toast.success('Added to wishlist!');
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    let cart = JSON.parse(localStorage.getItem('cart') || '[]');
    if (cart.some(item => item._id === product._id)) {
      toast.info('Product is already in your cart!');
      return;
    }
    cart.push(product);
    localStorage.setItem('cart', JSON.stringify(cart));
    setIsInCart(true);
    toast.success('Added to cart!');
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={product ? `${product.title} | YTProud` : 'Product | YTProud'}
        description={product ? (product.description || `Buy ${product.title} - ${product.accountType} account with ${product.stats?.followers || 0} followers`) : 'Product details'}
        keywords={product ? `${product.title}, ${product.accountType}, social media account, buy account, followers` : 'social media account'}
        canonical={`https://ytproud.com/product/${product?.slug || slug}`}
        ogTitle={product ? `${product.title} | YTProud` : 'Product | YTProud'}
        ogDescription={product ? (product.description || `Buy ${product.title} - ${product.accountType} account`) : 'Product details'}
        ogImage={product?.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage}`) : '/images/og-default.jpg'}
        twitterTitle={product ? `${product.title} | YTProud` : 'Product | YTProud'}
        twitterDescription={product ? (product.description || `Buy ${product.title} - ${product.accountType} account`) : 'Product details'}
        twitterImage={product?.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage}`) : '/images/twitter-default.jpg'}
        schemaJsonLd={product ? {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.title,
          "description": product.description,
          "image": product.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage}`) : '',
          "offers": {
            "@type": "Offer",
            "price": product.price,
            "priceCurrency": "USD",
            "availability": product.stockStatus === 'outofstock' ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
          },
          "brand": {
            "@type": "Brand",
            "name": product.accountType
          }
        } : null}
      />
      <Header />
      {/* Modern Card-Style Header - Full Width */}
      <div className="relative z-10 mt-4 sm:mt-8">
        <div className="max-w-[1495px] w-full mx-auto px-3 sm:px-4">
          <div className={`rounded-xl sm:rounded-2xl shadow-lg px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 transition-all duration-500 ${
            isDarkMode 
              ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
              : 'bg-white'
          }`}>
            {/* Breadcrumbs - Mobile Optimized */}
            <div className={`flex items-center text-xs sm:text-sm gap-1 sm:gap-2 overflow-hidden transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-400'
            }`}>
              <span className={`cursor-pointer truncate transition-colors duration-300 ${
                isDarkMode ? 'hover:text-white' : 'hover:text-black'
              }`}>Marketplace</span>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className={`cursor-pointer truncate transition-colors duration-300 ${
                isDarkMode ? 'hover:text-white' : 'hover:text-black'
              }`}>
                {product.accountType === 'youtube' ? 'Youtube Channels' : 
                 product.accountType ? product.accountType.charAt(0).toUpperCase() + product.accountType.slice(1) + ' Accounts' : ''}
              </span>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className={`font-semibold truncate min-w-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>{product.title}</span>
            </div>
            {/* Cart and Wishlist Icons - Mobile Optimized */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Cart Icon */}
              <button
                className={`inline-flex items-center justify-center p-2.5 sm:p-2 rounded-full shadow transition ${isInCart ? 'ring-2 ring-blue-400' : ''} ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                title={isInCart ? 'In Cart' : 'Add to Cart'}
                onClick={handleAddToCart}
              >
                <svg className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`} fill={isInCart ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61l1.38-7.39H6" />
                </svg>
              </button>
              {/* Wishlist Icon */}
              <button
                className={`inline-flex items-center justify-center p-2.5 sm:p-2 rounded-full shadow transition ${isWishlisted ? 'ring-2 ring-red-400' : ''} ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                onClick={handleToggleWishlist}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16.5 3c-1.74 0-3.41 1.01-4.5 2.09C10.91 4.01 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54a2 2 0 0 0 2.9 0C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="flex flex-col xl:flex-row gap-8 mx-auto py-8 px-4" style={{ maxWidth: '1495px' }}>
        {/* Product Box */}
        <div className={`flex-1 rounded-xl shadow-lg p-8 border-t-[6px] border-red-500 rounded-t-2xl transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
            : 'bg-white'
        }`}>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Image & Gallery */}
            <div className="flex-1 w-full">
              {isMobileCategory ? (
                <div className="relative flex flex-col items-center">
                  <div
                    key={sliderIndex}
                    className="relative w-full aspect-[9/16] bg-black rounded-3xl overflow-hidden flex items-center justify-center shadow-lg cursor-pointer zoom-image-container"
                    onClick={() => openModal(sliderIndex)}
                    title="Click to zoom"
                  >
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={sliderIndex}
                        src={allImages[sliderIndex]}
                        alt="Product Mobile"
                        className="w-full h-auto object-cover zoom-image"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                      />
                    </AnimatePresence>
                    <span className="zoom-icon-overlay">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                        <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                    {/* Left Arrow */}
                    <button
                      onClick={e => { e.stopPropagation(); handleSliderPrev(); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow hover:bg-gray-200 transition border border-gray-200 z-10"
                      aria-label="Previous"
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <polyline points="14 5 8 11 14 17" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    {/* Right Arrow */}
                    <button
                      onClick={e => { e.stopPropagation(); handleSliderNext(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow hover:bg-gray-200 transition border border-gray-200 z-10"
                      aria-label="Next"
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <polyline points="8 5 14 11 8 17" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                  </div>
                  {/* Dots Indicator - now inside the card, just below the frame */}
                  <div className="flex justify-center gap-2 mt-4">
                    {allImages.map((_, idx) => (
                      <span
                        key={idx}
                        onClick={() => setSliderIndex(idx)}
                        className={`w-3 h-3 rounded-full cursor-pointer ${sliderIndex === idx ? 'bg-red-500' : 'bg-gray-300'} transition`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
              {mainImage && (
                    <a onClick={() => openModal(0)} className="cursor-pointer block zoom-image-container w-full">
                      <img src={mainImage} alt="Product" className="zoom-image rounded-2xl border shadow-lg w-full h-auto max-h-[170px] object-cover mb-6 bg-white" />
                      <span className="zoom-icon-overlay">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                          <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                      </span>
              </a>
              )}
              {gallery.length > 0 && (
                <div>
                      <div className="font-semibold mb-3 text-lg">Attachments</div>
                      <div className="grid grid-cols-3 gap-4">
                    {gallery.map((img, i) => (
                          <a key={i} onClick={() => openModal(i + 1)} className="cursor-pointer block zoom-image-container">
                            <img src={img} alt="Gallery" className="zoom-image w-[140px] h-[90px] object-cover rounded-2xl border shadow bg-white" />
                            <span className="zoom-icon-overlay">
                              <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                                <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                            </span>
                      </a>
                    ))}
                  </div>
                </div>
                  )}
                </>
              )}
            </div>
            {/* Product Info */}
            <div className="flex-[2] min-w-[300px] w-full">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <h2 className={`product-title text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-0 break-words leading-tight transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{product.title}</h2>
                {product.accountUrl && (
                  <a href={product.accountUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-500 underline hover:text-red-500 text-sm sm:text-base font-medium mt-1 sm:mt-0 transition-colors duration-300">
                    <span role="img" aria-label="link">🔗</span>
                    <span>Check Profile</span>
                  </a>
                )}
              </div>
              {/* Description with Read More/Less */}
              <div className={`product-short-description mb-4 text-sm sm:text-base leading-relaxed px-1 sm:px-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`} style={{ minHeight: '60px' }}>
                <div className="description-content" style={{ fontWeight: 'normal' }}>
                  {showFullDesc ? description : truncatedDescription}
                </div>
                {isLongDescription && (
                  <button className={`font-bold mt-2 text-xs sm:text-sm underline transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`} onClick={() => setShowFullDesc(!showFullDesc)}>
                    {showFullDesc ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
              {/* Info Grid */}
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 text-center text-xs sm:text-sm transition-all duration-300 ${
                isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.followers || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Followers</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.monetized || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Monetized</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.category || product.accountType || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Category</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.strikes || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Strikes</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.income || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Month Income</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.videos || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Videos</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.views || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Total Views</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{stats.created || '-'}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Created</span></div>
              </div>
              {/* Price, Buy Now, Payment, Delivery */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-8 mb-2 mt-6 sm:mt-8">
                {/* Left: Price, Buy Now */}
                <div className="flex flex-col items-start gap-2 flex-1 min-w-[160px] w-full">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className={`text-2xl sm:text-3xl font-extrabold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>USD ${product.salePrice ? product.salePrice : product.price}</span>
                    {product.salePrice && <span className={`text-lg sm:text-xl line-through transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>${product.price}</span>}
                  </div>
                  {(!('stockStatus' in product) || !('quantity' in product) || (product.stockStatus === 'instock' && product.quantity > 0)) ? (
                    <button
                      className="mt-2 w-full sm:w-48 h-12 sm:h-14 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-lg sm:text-xl shadow transition-all flex items-center justify-center"
                      onClick={() => navigate('/checkout', { state: { product } })}
                    >
                      Buy Now
                    </button>
                  ) : (
                    <button
                      className="mt-2 w-full sm:w-48 h-12 sm:h-14 bg-gray-500 text-white font-bold rounded-xl text-lg sm:text-xl shadow flex items-center justify-center cursor-not-allowed"
                      disabled
                    >
                      Out of Stock
                    </button>
                  )}
                </div>
                {/* Right: Delivery, Secure, Payment Icons */}
                <div className="flex flex-col md:items-end gap-2 flex-1 min-w-[160px] w-full mt-4 sm:mt-0">
                  <div className="flex flex-row gap-3 sm:gap-6 mb-2 md:justify-end flex-nowrap whitespace-nowrap">
                    <div className={`flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {/* Truck icon */}
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 17V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v12"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/><path d="M21 17V9a2 2 0 0 0-2-2h-4"/></svg>
                      <span className="whitespace-nowrap">Fast Delivery</span>
                    </div>
                    <div className={`flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {/* Shield icon */}
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      <span className="whitespace-nowrap">Secure Transaction</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2 sm:gap-3 mt-2 payment-icons md:justify-end">
                    {paymentIconsFooter.map((icon, i) => (
                      <span key={i} title={icon.label} className="inline-flex items-center justify-center">{icon.svg}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Seller Box */}
        <div className={`w-full max-w-full xl:w-[340px] xl:max-w-[340px] rounded-2xl shadow-lg p-5 sm:p-7 flex-shrink-0 flex flex-col justify-between border-t-[6px] border-red-500 rounded-t-2xl mx-0 transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
            : 'bg-white'
        }`}>
          <h3 className={`text-2xl font-bold mb-5 transition-colors duration-300 flex items-center gap-3 ${
            isDarkMode ? 'text-white' : 'text-black'
          }`}>
            <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
            <span className={`transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}>Seller Details</span>
            <div className="flex-1 h-0.5 bg-gradient-to-r from-red-500/50 to-transparent rounded-full"></div>
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <a href={`/profile/${seller.username}`} target="_blank" rel="noopener noreferrer">
              {seller.avatar && seller.avatar.startsWith('data:image') ? (
                <img src={seller.avatar} alt="Seller" className="w-14 h-14 rounded-full object-cover border" />
              ) : seller.avatar && seller.avatar.startsWith('http') ? (
                <img src={seller.avatar} alt="Seller" className="w-14 h-14 rounded-full object-cover border" />
              ) : (
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold border transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {seller.username ? seller.username[0].toUpperCase() : 'S'}
                </div>
              )}
            </a>
            <div>
              <div className={`flex items-center gap-2 font-bold text-lg transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <a href={`/profile/${seller.username}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{seller.username}</a>
                {seller.verified && (
                  <span title="Verified Seller">
                    {/* Blue tick SVG */}
                    <svg className="inline-block align-middle" width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#2563eb"/><path d="M6 10.5l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                )}
              </div>
              <button className="mt-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg px-5 py-2 text-base shadow transition" onClick={handleContactSeller} disabled={contactLoading || !sellerDetails}>
                {contactLoading ? 'Starting chat...' : 'Contact Seller'}
              </button>
              {!sellerDetails && (
                <div className="text-red-500 text-sm mt-2">Seller details not loaded. Please check the console for debug info.</div>
              )}
            </div>
          </div>
          {/* Ratings summary card - move to top */}
          <div className="mb-6">
            <div className={`rounded-2xl shadow-md px-7 py-6 text-center border transition-all duration-300 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] border border-gray-700/50 shadow-lg shadow-black/30' 
                : 'bg-white border-gray-100'
            }`}>
              <div className={`font-bold text-lg mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Seller's Ratings</div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-4xl font-extrabold text-red-600">{seller.rating}</span>
                <span className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-6 h-6" fill={i < Math.round(seller.rating) ? '#da1e27' : '#e5e7eb'} viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z"/></svg>
                  ))}
                </span>
              </div>
              <div className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}>Based on <a href="#" className="text-blue-500 underline font-semibold">{seller.reviews} reviews</a></div>
            </div>
          </div>
          <div className={`font-semibold mb-2 mt-2 text-lg transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Seller Profile Details</div>
          <div className={`text-base mb-1 font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>Joined Date <span className={`font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>{seller.joined}</span></div>
          <div className={`text-base mb-3 font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>Made {seller.deals} deals worth <span className={`font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>${seller.total.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
        </div>
      </div>
      {/* Related Products Section - Consistent with other pages */}
      <div className="mx-auto px-3 sm:px-4 mt-8 sm:mt-12 mb-6 sm:mb-8" style={{ maxWidth: '1495px' }}>
        {/* Section Header - Consistent with Home page */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 w-full">
          <h2 className={`text-xl sm:text-2xl md:text-[30px] font-bold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>Related Products</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
          {/* Navigation Controls - Mobile Optimized */}
          <div className="flex gap-2">
            <button
              className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-full border shadow transition disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' 
                  : 'border-gray-200 bg-white hover:bg-gray-100'
              }`}
              onClick={() => setRelatedPage((prev) => Math.max(prev - 1, 0))}
              disabled={relatedPage === 0}
            >
              {/* Left Arrow */}
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-full border shadow transition disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' 
                  : 'border-gray-200 bg-white hover:bg-gray-100'
              }`}
              onClick={() => setRelatedPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={relatedPage >= totalPages - 1}
            >
              {/* Right Arrow */}
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Products Grid - Responsive Layout - Consistent with other pages */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto items-stretch">
          {relatedProductsLoading ? (
            // Show skeleton loading cards
            [...Array(PRODUCTS_PER_PAGE)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-full flex flex-col transform transition-all duration-200">
                <ProductCardSkeleton />
              </div>
            ))
          ) : (
            // Show actual products
            relatedProducts
              .slice(relatedPage * PRODUCTS_PER_PAGE, (relatedPage + 1) * PRODUCTS_PER_PAGE)
              .map((p, i) => (
                <div key={p._id || i + relatedPage * PRODUCTS_PER_PAGE} className="h-full flex flex-col transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg">
                  <ProductCard
                    product={p}
                    width={360}
                  />
                </div>
              ))
          )}
        </div>
        
        {/* Mobile Pagination Dots */}
        <div className="flex justify-center mt-6 sm:hidden">
          <div className="flex gap-2">
            {relatedProductsLoading ? (
              // Show skeleton dots when loading
              [...Array(3)].map((_, idx) => (
                <div
                  key={`dot-skeleton-${idx}`}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                />
              ))
            ) : (
              // Show actual pagination dots
              [...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setRelatedPage(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    relatedPage === idx ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {/* Modal for image viewing */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <button
            onClick={closeModal}
            className="absolute top-6 right-8 flex items-center justify-center w-12 h-12 rounded-xl border border-white/60 bg-gray-700/70 hover:bg-gray-700/90 transition shadow-lg focus:outline-none"
            aria-label="Close"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="8" y1="8" x2="20" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              <line x1="20" y1="8" x2="8" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg hover:bg-gray-200 transition border border-gray-200"
            aria-label="Previous"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="17 7 11 14 17 21" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </button>
          <img src={allImages[modalIndex]} alt="Product Large" className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl border-4 border-white" />
          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg hover:bg-gray-200 transition border border-gray-200"
            aria-label="Next"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="11 7 17 14 11 21" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </button>
        </div>
      )}
      <Footer />
      {/* Add styles for zoom and overlay icon */}
      <style jsx>{`
        .zoom-image-container {
          position: relative;
          display: inline-block;
          overflow: hidden;
          border-radius: 1rem;
        }
        .zoom-image {
          transition: transform 0.3s cubic-bezier(.4,2,.6,1);
          will-change: transform;
          display: block;
        }
        .zoom-image-container:hover .zoom-image {
          transform: scale(1.08);
        }
        .zoom-icon-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 2;
        }
        .zoom-image-container:hover .zoom-icon-overlay {
          opacity: 1;
        }
      `}</style>
    </div>
  );
}

export default ProductView; 